import { create } from 'zustand';
import { temporal } from 'zundo';
import { useStoreWithEqualityFn } from 'zustand/traditional';
import { createJSONStorage, persist } from 'zustand/middleware';
import debounce from 'lodash.debounce';
import generateUUID from '@picsart/utils/generateUUID';
import { nanoid } from 'nanoid';

import {
  AI_WRITER_EDITOR_LAST_TEXT,
  AI_WRITER_EDITOR_ID,
  AI_WRITER_EDITOR_SUBCATEGORY,
} from '../../configs';

import type { TemporalState } from 'zundo';

const MAX_VALUE_LENGTH = 10000;
const MAX_VALUE_LENGTH_ERROR_MSG = 'playgroundEditor.error.maxLength';
const isClient = typeof window !== 'undefined';

const generateEditorId = (isBrowser: boolean) => {
  if (!isBrowser) {
    return nanoid();
  }

  const currentId = sessionStorage.getItem(AI_WRITER_EDITOR_ID);

  if (currentId) return currentId;

  const newId = generateUUID();
  sessionStorage.setItem(AI_WRITER_EDITOR_ID, newId);
  return newId;
};

const getSubcategoryStorageKey = (subcategory?: number) =>
  subcategory == null ? AI_WRITER_EDITOR_LAST_TEXT : `${AI_WRITER_EDITOR_LAST_TEXT}-${subcategory}`;

type State = {
  id: string;
  text: string;
  isOpen: boolean;
  error: string | null;
  currentSubcategory?: number;
};

type Actions = {
  open: () => void;
  toggleOpen: () => void;
  pasteText: (text: string) => void;
  writeText: (text: string, id?: string) => void;
  setError: (error: string | null) => void;
  setSubcategory: (subcategory?: number) => void;
};

const initialState = {
  error: null,
  isOpen: false,
  id: generateEditorId(isClient),
  text: '',
  currentSubcategory: undefined,
};

export const useEditorStore = create<State & Actions>()(
  temporal(
    (set, get) => {
      return {
        ...initialState,
        open: () => {
          set({ isOpen: true });
        },
        toggleOpen: () => {
          set({ isOpen: !get().isOpen });
        },
        writeText: (text) => {
          if (text.length > MAX_VALUE_LENGTH) {
            set({ text: text.slice(0, MAX_VALUE_LENGTH), error: MAX_VALUE_LENGTH_ERROR_MSG });
          } else {
            set({ text, error: null });
          }
        },
        pasteText: (text) => {
          const prevText = get().text;

          if (prevText.length === MAX_VALUE_LENGTH) {
            return;
          }

          if (prevText.length + text.length > MAX_VALUE_LENGTH) {
            const remaining = MAX_VALUE_LENGTH - prevText.length - 2;
            const remainingText = text.slice(0, remaining);
            set({
              text: prevText ? `${prevText}\n\n${remainingText}` : text,
              error: MAX_VALUE_LENGTH_ERROR_MSG,
            });
          } else {
            set({ text: prevText ? `${prevText}\n\n${text}` : text, error: null });
          }
        },
        setError: (error) => {
          set({ error });
        },
        setSubcategory: (subcategory) => {
          const currentSubcategory = get().currentSubcategory;

          if (isClient && currentSubcategory !== undefined) {
            const currentText = get().text;
            const storageKey = getSubcategoryStorageKey(currentSubcategory);
            sessionStorage.setItem(storageKey, currentText);
          }

          let newText = '';
          if (isClient) {
            const storageKey = getSubcategoryStorageKey(subcategory);
            newText = sessionStorage.getItem(storageKey) || '';
            if (subcategory === undefined) {
              sessionStorage.removeItem(AI_WRITER_EDITOR_SUBCATEGORY);
            } else {
              sessionStorage.setItem(AI_WRITER_EDITOR_SUBCATEGORY, String(subcategory));
            }
          }

          set({
            currentSubcategory: subcategory,
            text: newText,
            error: null,
          });
        },
      };
    },
    {
      wrapTemporal: (storeInitializer) =>
        persist(storeInitializer, {
          name: 'ai-writer-editor',
          storage: createJSONStorage(() => sessionStorage),
        }),
      partialize: () => ({}),
      handleSet: (handleSet) =>
        debounce<typeof handleSet>(
          (state) => {
            handleSet(state);
          },
          500,
          {
            maxWait: 1000,
            leading: true,
            trailing: false,
          },
        ),
    },
  ),
);

export const useTemporalEditorStore = <T>(
  selector: (state: TemporalState<State & Actions>) => T,
  equality?: (a: T, b: T) => boolean,
) => useStoreWithEqualityFn(useEditorStore.temporal, selector, equality);
